import React, { useEffect, useState } from 'react';
import { Save, Building2, AlertTriangle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Modal } from '../components/ui';

export default function Settings() {
  const [s, setS] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  useEffect(() => { api.get('/settings').then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-400 animate-pulse">Loading settings…</div>;

  const setC = (k, v) => setS((p) => ({ ...p, company: { ...p.company, [k]: v } }));

  const save = async () => {
    try {
      await api.put('/settings', {
        company: s.company,
        invoiceFormat: s.invoiceFormat,
        defaultTailorRate: Number(s.defaultTailorRate),
      });
      toast.success('Settings saved');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Company profile & invoice preferences"
        actions={<Button onClick={save} data-testid="save-settings-btn"><Save size={16} /> Save Changes</Button>} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5"><Building2 size={16} className="text-[#1E3A8A]" /><span className="font-heading font-bold text-slate-800">Company Profile</span></div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Company Name" value={s.company.name} onChange={(e) => setC('name', e.target.value)} data-testid="company-name" />
            <Input label="Proprietor" value={s.company.proprietor || ''} onChange={(e) => setC('proprietor', e.target.value)} />
            <Input label="Arabic Name" value={s.company.nameAr || ''} onChange={(e) => setC('nameAr', e.target.value)} dir="rtl" />
            <Input label="Address" value={s.company.address} onChange={(e) => setC('address', e.target.value)} />
            <Input label="Arabic Address" value={s.company.addressAr || ''} onChange={(e) => setC('addressAr', e.target.value)} dir="rtl" />
            <Input label="Phone" value={s.company.phone} onChange={(e) => setC('phone', e.target.value)} />
            <Input label="Email" value={s.company.email} onChange={(e) => setC('email', e.target.value)} />
            <Input label="Tax Number (TRN)" value={s.company.taxNumber} onChange={(e) => setC('taxNumber', e.target.value)} />
            <Input label="Currency" value={s.company.currency} onChange={(e) => setC('currency', e.target.value)} />
          </div>
        </Card>

        <div className="space-y-6 self-start">
          <Card className="p-5">
            <span className="font-heading font-bold text-slate-800 block mb-5">Preferences</span>
            <div className="space-y-4">
              <Input label="Invoice Number Prefix" value={s.invoiceFormat} onChange={(e) => setS((p) => ({ ...p, invoiceFormat: e.target.value }))} data-testid="invoice-format" />
              <Input label="Default Tailor Rate (KWD/dozen)" type="number" step="0.5" value={s.defaultTailorRate} onChange={(e) => setS((p) => ({ ...p, defaultTailorRate: e.target.value }))} />
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Categories</p>
                <p className="text-sm text-slate-600">{s.categories?.join(', ')}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Units</p>
                <p className="text-sm text-slate-600">{s.units?.join(', ')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-rose-200 bg-rose-50/40">
            <div className="flex items-center gap-2 mb-3"><AlertTriangle size={16} className="text-rose-600" /><span className="font-heading font-bold text-rose-700">Danger Zone</span></div>
            <p className="text-sm text-slate-600 mb-4">Reset all business data — sales, purchases, expenses, inventory, production, tailors, customers and payments. <span className="font-semibold">Users and settings are kept.</span> This cannot be undone.</p>
            <Button variant="danger" onClick={() => setResetOpen(true)} data-testid="reset-data-btn"><RotateCcw size={15} /> Reset All Data</Button>
          </Card>
        </div>
      </div>

      {resetOpen && <ResetModal onClose={() => setResetOpen(false)} />}
    </div>
  );
}

function ResetModal({ onClose }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/reset');
      const total = Object.values(r.data.cleared).reduce((a, b) => a + b, 0);
      toast.success(`All data reset · ${total} records removed`);
      onClose();
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Modal open size="sm" title="Reset All Data" onClose={onClose}
      footer={<>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={text !== 'RESET' || busy} onClick={doReset} data-testid="confirm-reset-btn">
          {busy ? 'Resetting…' : 'Yes, Reset Everything'}
        </Button>
      </>}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">This permanently deletes <span className="font-semibold">all invoices, customers, purchases, expenses, inventory, production jobs, tailors and payment records</span>. Only user accounts and company settings remain. Invoice numbering restarts from 1.</p>
        <Input label='Type "RESET" to confirm' value={text} onChange={(e) => setText(e.target.value)} placeholder="RESET" data-testid="reset-confirm-input" />
      </div>
    </Modal>
  );
}
